import { useState } from 'react';
import { emailTemplates, sentEmails, emailStats, EmailTemplate, SentEmail, EmailStatus } from '../../mocks/emails';
import TemplateCard from './components/TemplateCard';
import EmailPreview from './components/EmailPreview';
import EmailComposer from './components/EmailComposer';
import SentEmailsTable from './components/SentEmailsTable';
import { agencyInfo } from '../../mocks/dashboard';

const brand = agencyInfo.brandColor;

type Tab = 'templates' | 'compose' | 'sent';

const statusFilters: { label: string; value: string }[] = [
  { label: 'All', value: 'all' },
  { label: 'Sent', value: 'Sent' },
  { label: 'Delivered', value: 'Delivered' },
  { label: 'Opened', value: 'Opened' },
  { label: 'Clicked', value: 'Clicked' },
  { label: 'Bounced', value: 'Bounced' },
];

export default function EmailPage() {
  const [activeTab, setActiveTab] = useState<Tab>('templates');
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [composerTemplate, setComposerTemplate] = useState<EmailTemplate | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sentFilter, setSentFilter] = useState('all');
  const [sentSearch, setSentSearch] = useState('');
  const [sentList, setSentList] = useState<SentEmail[]>(sentEmails);
  const [toastMsg, setToastMsg] = useState('');

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleUseTemplate = (template: EmailTemplate) => {
    setComposerTemplate(template);
    setComposerOpen(true);
    setPreviewTemplate(null);
  };

  const handleResend = (email: SentEmail) => {
    const tmpl = emailTemplates.find((t) => t.type === email.templateType) || null;
    setComposerTemplate(tmpl);
    setComposerOpen(true);
  };

  const handleSent = (toName: string, subject: string) => {
    const now = new Date().toISOString();
    const newEmail: SentEmail = {
      id: `em-${Date.now()}`,
      to: 'customer@email.com',
      toName,
      subject,
      templateType: composerTemplate?.type || 'custom',
      templateName: composerTemplate?.name || 'Custom Email',
      sentAt: now,
      sentBy: 'Mohammed Khalid',
      status: 'Sent',
      attachments: [],
    };
    setSentList((prev) => [newEmail, ...prev]);
    showToast(`Email sent to ${toName} successfully!`);
    setActiveTab('sent');
  };

  const filteredSent = sentList
    .filter((e) => sentFilter === 'all' || e.status === sentFilter)
    .filter((e) =>
      !sentSearch ||
      e.toName.toLowerCase().includes(sentSearch.toLowerCase()) ||
      e.subject.toLowerCase().includes(sentSearch.toLowerCase()) ||
      e.templateName.toLowerCase().includes(sentSearch.toLowerCase())
    );

  const statCards = [
    { label: 'Total Sent', value: emailStats.totalSent.toLocaleString(), icon: 'ri-send-plane-2-line', color: '#6366f1' },
    { label: 'Delivery Rate', value: `${emailStats.deliveryRate}%`, icon: 'ri-mail-check-line', color: '#10b981' },
    { label: 'Open Rate', value: `${emailStats.openRate}%`, icon: 'ri-mail-open-line', color: '#f59e0b' },
    { label: 'Click Rate', value: `${emailStats.clickRate}%`, icon: 'ri-cursor-line', color: '#ec4899' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Toast */}
      {toastMsg && (
        <div
          className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm shadow-lg"
          style={{ backgroundColor: brand }}
        >
          <i className="ri-checkbox-circle-line text-lg" />
          {toastMsg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Email System</h1>
            <p className="text-sm text-gray-400 mt-0.5">Send branded emails to customers — confirmations, invoices, itineraries & more</p>
          </div>
          <button
            onClick={() => { setComposerTemplate(null); setComposerOpen(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-add-line" />
            Compose Email
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4 mt-5">
          {statCards.map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: s.color + '15', color: s.color }}
              >
                <i className={`${s.icon} text-lg`} />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-5 bg-gray-100 rounded-xl p-1 w-fit">
          {([
            { key: 'templates', label: 'Email Templates', icon: 'ri-layout-3-line' },
            { key: 'compose', label: 'Quick Compose', icon: 'ri-edit-2-line' },
            { key: 'sent', label: `Sent (${sentList.length})`, icon: 'ri-send-plane-line' },
          ] as { key: Tab; label: string; icon: string }[]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-8 py-6">
        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="font-semibold text-gray-800">Branded Email Templates</p>
                <p className="text-sm text-gray-400 mt-0.5">{emailTemplates.length} ready-to-use templates — click any to preview or compose</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {emailTemplates.map((tmpl) => (
                <TemplateCard
                  key={tmpl.id}
                  template={tmpl}
                  onUse={handleUseTemplate}
                  onPreview={setPreviewTemplate}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick Compose Tab */}
        {activeTab === 'compose' && (
          <div className="max-w-2xl">
            <p className="font-semibold text-gray-800 mb-1">Quick Compose</p>
            <p className="text-sm text-gray-400 mb-5">Pick a template type and we'll open the composer with the right layout</p>
            <div className="grid grid-cols-2 gap-3">
              {emailTemplates.map((tmpl) => (
                <button
                  key={tmpl.id}
                  onClick={() => handleUseTemplate(tmpl)}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 text-left transition-all cursor-pointer group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
                    style={{ backgroundColor: tmpl.bgColor, color: tmpl.color }}
                  >
                    <i className={`${tmpl.icon} text-xl`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{tmpl.name}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{tmpl.description.substring(0, 55)}...</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-500 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sent Tab */}
        {activeTab === 'sent' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-semibold text-gray-800">Sent Email History</p>
                <p className="text-sm text-gray-400 mt-0.5">{filteredSent.length} emails &bull; Track open & click rates</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={sentSearch}
                    onChange={(e) => setSentSearch(e.target.value)}
                    placeholder="Search emails..."
                    className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-300 w-52"
                  />
                </div>
              </div>
            </div>

            {/* Status filter pills */}
            <div className="flex gap-1.5 mb-4 flex-wrap">
              {statusFilters.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setSentFilter(f.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sentFilter === f.value ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  }`}
                  style={sentFilter === f.value ? { backgroundColor: brand } : {}}
                >
                  {f.label}
                  {f.value !== 'all' && (
                    <span className="ml-1 opacity-70">
                      ({sentList.filter((e) => e.status === f.value).length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {filteredSent.length > 0 ? (
              <SentEmailsTable emails={filteredSent} onResend={handleResend} />
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
                <i className="ri-mail-send-line text-4xl text-gray-200 mb-3 block" />
                <p className="text-gray-400 text-sm">No emails found matching your filters</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Preview Modal */}
      {previewTemplate && (
        <EmailPreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUse={() => handleUseTemplate(previewTemplate)}
        />
      )}

      {/* Composer Modal */}
      {composerOpen && (
        <EmailComposer
          template={composerTemplate}
          onClose={() => setComposerOpen(false)}
          onSent={handleSent}
        />
      )}
    </div>
  );
}
